import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modals/Modal';
import { PrimaryButton } from '@/components/ui/Buttons/Primary';
import { SecondaryButton } from '@/components/ui/Buttons/Secondary';
import { UPGRADE_PROMPTS } from '@/constants/upgradePrompts';
import designTokens from '@/design-tokens';

interface PlanUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureKey: string;
}

export const PlanUpgradeModal: React.FC<PlanUpgradeModalProps> = ({
  isOpen,
  onClose,
  featureKey,
}) => {
  const navigate = useNavigate();
  const config = UPGRADE_PROMPTS[featureKey];
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 439px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setIsNarrow(e.matches);
    handler(mql);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  if (!config) return null;

  const handleViewPricing = () => {
    onClose();
    navigate('/settings/subscription');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" maxHeight="auto">
      <Modal.Header showCloseButton onClose={onClose}>
        <span />
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
          }}
        >
          {/*
            * Rendered only when the prompt carries artwork. Without this guard an
            * illustration-less prompt renders a broken image inside a 180px box —
            * the block is dropped whole rather than reserved, so the modal closes
            * up around the copy instead of leaving a hole. Prompts that DO carry
            * an illustration are unchanged.
            */}
          {config.illustration && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                minHeight: '180px',
              }}
            >
              <img
                src={config.illustration}
                alt=""
                style={{
                  maxWidth: '240px',
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            </div>
          )}

          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <h2
              style={{
                margin: 0,
                fontFamily: designTokens['font/family/display'] || designTokens['font/family/body'],
                fontSize: designTokens['font/size/xxl'] || '1.25rem',
                fontWeight: designTokens['font/weight/semibold'] || 600,
                color: designTokens['text/primary'] || '#000000',
                lineHeight: 1.25,
              }}
            >
              {config.headline}
            </h2>
            <p
              style={{
                margin: 0,
                fontFamily: designTokens['font/family/body'],
                fontSize: designTokens['font/size/md'] || '1rem',
                color: designTokens['text/primary'] || '#000000',
                lineHeight: 1.6,
              }}
            >
              {config.body}
            </p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer align={isNarrow ? 'center' : 'right'}>
        {isNarrow ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
            <PrimaryButton size="large" onClick={handleViewPricing} style={{ width: '100%' }}>
              Upgrade Now
            </PrimaryButton>
            <SecondaryButton size="large" onClick={onClose} style={{ width: '100%' }}>
              Maybe Later
            </SecondaryButton>
          </div>
        ) : (
          <>
            <SecondaryButton onClick={onClose}>Maybe Later</SecondaryButton>
            <PrimaryButton onClick={handleViewPricing}>Upgrade Now</PrimaryButton>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PlanUpgradeModal;
